
import React, { useEffect, useState } from 'react';
import { Flex, Switch, Table, Tag, Transfer, Button } from 'antd';
import type { GetProp, TableColumnsType, TableProps, TransferProps } from 'antd';
import { TableRowSelection } from 'antd/es/table/interface';
import { RoleItem } from 'pages/Role';
import { API_URL } from 'env';


interface DataType {
    key: string;
    id: string;
    userName: string;
    phoneNumber: string;
    email: string;
}
const columns: TableColumnsType<DataType> = [
    {
        title: 'User ID',
        dataIndex: 'id',
        key: 'id',
        render: (text: string) => <span className="text-sm">{text}</span>,
    },
    {
        title: 'User Name',
        dataIndex: 'userName',
        key: 'userName',
        render: (text: string) => <span className="text-sm">{text}</span>,
    },
    {
        title: 'Phone Number',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        render: (text: string) => <span className="text-sm">{text}</span>,
    },
    {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (text: string) => <span className="text-sm">{text}</span>,
    },
];

const AddUsersToRoleInterface = ({ role , onClose}: { role: RoleItem , onClose:() => void}) => {

    const [assignUsers, setAssignUsers] = useState<DataType[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [defaultSelectedUser, setDefaultSelectedUser] = useState<React.Key[]>([]);
    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };
    const rowSelection: TableRowSelection<DataType> = {
        selectedRowKeys,
        onChange: onSelectChange,
        selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
            {
                key: 'odd',
                text: 'Select Odd Row',
                onSelect: (changeableRowKeys) => {
                    let newSelectedRowKeys = [];
                    newSelectedRowKeys = changeableRowKeys.filter((_, index) => {
                        if (index % 2 !== 0) {
                            return false;
                        }
                        return true;
                    });
                    setSelectedRowKeys(newSelectedRowKeys);
                },
            },
            {
                key: 'even',
                text: 'Select Even Row',
                onSelect: (changeableRowKeys) => {
                    let newSelectedRowKeys = [];
                    newSelectedRowKeys = changeableRowKeys.filter((_, index) => {
                        if (index % 2 !== 0) {
                            return true;
                        }
                        return false;
                    });
                    setSelectedRowKeys(newSelectedRowKeys);
                },
            },
        ],
    };

    async function onAddUserToRole(id: number) {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/assign-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    role_id: role.id,
                    user_id: id,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to assign users');
            }
            const data = await response.json();
            console.log('Users assigned successfully:', data);
        } catch (error) {
            console.error('Error assigning users:', error);
        }
    }

    async function onRemoveRoleFromUser(id: number) {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/remove-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    role_id: role.id,
                    user_id: id,
                }),
            });
            if (!response.ok) {
                throw new Error('Failed to remove users');
            }
            const data = await response.json();
            console.log('Users removed successfully:', data);
        } catch (error) {
            console.error('Error removing users:', error);
        }
    }

    async function onSave(role: number) {
        try {
            const usersToAdd = selectedRowKeys.filter(key => !defaultSelectedUser.includes(key));
            const usersToRemove = defaultSelectedUser.filter(key => !selectedRowKeys.includes(key));

            await Promise.all([
                ...usersToAdd.map(async (userId) => await onAddUserToRole(Number(userId))),
                ...usersToRemove.map(async (userId) => await onRemoveRoleFromUser(Number(userId))),
            ]);
            setDefaultSelectedUser(selectedRowKeys);
            onClose(); 

        } catch (error) {
            console.error('Error assigning users:', error);
        }
    }
    async function getAssignUsers() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role/${role.id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                console.error('Failed to fetch users:', response.statusText);
                return;
            }
            const data = await response.json();
            const users: DataType[] = data.data.users.map((item: any) => ({
                key: item.id,
                id: item.id,
                userName: item.fullname,
                phoneNumber: item.phone_number || '',
                email: item.email,
            }));
            setSelectedRowKeys(users.map(user => user.key));
            setDefaultSelectedUser(users.map(user => user.key));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }

    async function fetchData() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/users`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) {
                console.error('Failed to fetch users:', response.statusText);
                return;
            }
            const data = await response.json();
            const users: DataType[] = data.data.items.map((item: any) => ({
                key: item.id,
                id: item.id,
                userName: item.fullname,
                phoneNumber: item.phone_number || '',
                email: item.email,
            }));
            setAssignUsers(users);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }
    useEffect(() => {
        fetchData();
        getAssignUsers();
    }, []);

    return (
        <div className="flex flex-col gap-4 h-[80vh] overflow-y-auto "
            style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
                overflowY: 'auto',
                scrollbarColor: 'transparent transparent',
            }}>
            <h2 className="text-xl font-semibold">Add Users to Role</h2>
            <div className="flex flex-col gap-4 h-full">
                <Table rowSelection={rowSelection} columns={columns} dataSource={assignUsers} className='' />
            </div>
            <Flex justify="flex-end" className="mt-4">
                <Button type="primary" onClick={() => {
                    onSave(role.id);
                }}>
                    Assign Selected Users
                </Button>
            </Flex>
        </div>
    );
}
export default AddUsersToRoleInterface;